package com.tovtalk.data.cache

import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

class SessionVideoCache(
    private val maxSizeBytes: Long = DEFAULT_MAX_SIZE
) {
    private val mutex = Mutex()
    // LinkedHashMap in KMP common is insertion-order only (no access-order constructor).
    // We simulate LRU by removing + re-inserting on access.
    private val cache = LinkedHashMap<String, ByteArray>()
    private var currentSizeBytes: Long = 0

    suspend fun get(url: String): ByteArray? = mutex.withLock {
        val key = S3CacheKeyResolver.resolve(url)
        // Re-insert on access to maintain LRU order (most-recently-used at end).
        cache.remove(key)?.also { cache[key] = it }
    }

    suspend fun put(url: String, data: ByteArray) = mutex.withLock {
        val key = S3CacheKeyResolver.resolve(url)
        cache.remove(key)?.let { currentSizeBytes -= it.size }
        while (currentSizeBytes + data.size > maxSizeBytes && cache.isNotEmpty()) {
            val eldest = cache.entries.first()
            currentSizeBytes -= eldest.value.size
            cache.remove(eldest.key)
        }
        cache[key] = data
        currentSizeBytes += data.size
    }

    suspend fun contains(url: String): Boolean = mutex.withLock { cache.containsKey(S3CacheKeyResolver.resolve(url)) }

    suspend fun clear() = mutex.withLock {
        cache.clear()
        currentSizeBytes = 0
    }

    companion object {
        const val DEFAULT_MAX_SIZE = 100L * 1024 * 1024  // 100MB
        const val LOW_MEMORY_MAX_SIZE = 50L * 1024 * 1024 // 50MB
    }
}
